import {
  ArrowRight,
  BookOpenCheck,
  Check,
  Clock3,
  Languages,
  MapPin,
  MessageCircleQuestion,
  Sigma,
  Sparkles,
  Users2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'Ce cursuri sunt disponibile la Centrul Progres?',
    answer: 'Organizăm cursuri de limbi străine pentru clasele I-XII și meditații la limba română și matematică pentru clasele V-XII.'
  },
  {
    question: 'Ședințele sunt individuale sau în grupă?',
    answer: 'La limbi străine poți alege între ședințe în grupă și ședințe individuale. Româna și matematica se desfășoară doar în grupe.'
  },
  {
    question: 'Cât durează o ședință?',
    answer: 'Ședințele de limbi străine durează 1,5 ore. Ședințele de română și matematică durează 2 ore.'
  },
  {
    question: 'Unde se află centrul?',
    answer: 'Ne găsești în București, pe Șoseaua Colentina nr. 16, în Complexul Rose Garden, Bloc B4, la parter.'
  }
];

const Home = () => (
  <div className="overflow-hidden">
    <section className="relative bg-blue-600 text-white">
      <div className="absolute -left-24 top-24 h-72 w-72 rounded-full bg-blue-400/35 blur-3xl" aria-hidden="true" />
      <div className="absolute right-8 top-10 flex h-28 w-28 rotate-12 items-center justify-center rounded-[2rem] bg-sun text-ink" aria-hidden="true">
        <Sigma className="h-12 w-12 -rotate-12" strokeWidth={2.4} />
      </div>
      <div className="absolute bottom-8 left-[48%] flex h-16 w-16 items-center justify-center rounded-full bg-coral text-white" aria-hidden="true">
        <Languages className="h-8 w-8" strokeWidth={2.4} />
      </div>

      <div className="relative mx-auto grid min-h-[min(780px,calc(100vh-108px))] max-w-7xl items-center gap-12 px-5 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:py-20">
        <div className="max-w-2xl animate-reveal">
          <p className="mb-7 inline-flex items-center gap-2 rounded-full bg-ink/20 px-4 py-2 text-sm font-bold text-blue-50">
            <MapPin className="h-4 w-4 text-sun" />
            Meditații și limbi străine în București
          </p>
          <h1 className="font-display text-[clamp(3.6rem,8vw,7.4rem)] font-bold leading-[0.88] tracking-[-0.055em]">
            Mai mult curaj la școală.
            <span className="mt-3 block text-sun">Mai multă claritate acasă.</span>
          </h1>
          <p className="mt-8 max-w-xl text-lg leading-relaxed text-blue-50 sm:text-xl">
            Cursuri de limbi străine, română și matematică, cu explicații pe înțelesul elevilor și progres urmărit pas cu pas.
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Link to="/packages" className="inline-flex items-center justify-center gap-2 rounded-full bg-sun px-7 py-4 font-bold text-ink shadow-lift transition-all duration-300 hover:-translate-y-1 hover:bg-yellow-300">
              Descoperă cursurile <ArrowRight className="h-5 w-5" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center rounded-full border border-white/40 px-7 py-4 font-bold text-white transition-colors hover:bg-white/10">
              Vorbește cu un profesor
            </Link>
          </div>

          <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm font-semibold text-blue-50" aria-label="Avantaje principale">
            <li className="flex items-center gap-2"><Check className="h-5 w-5 text-sun" /> Clasele I-XII</li>
            <li className="flex items-center gap-2"><Check className="h-5 w-5 text-sun" /> Grupe restrânse</li>
            <li className="flex items-center gap-2"><Check className="h-5 w-5 text-sun" /> Program flexibil</li>
          </ul>
        </div>

        <div className="relative mx-auto w-full max-w-xl lg:max-w-none">
          <div className="relative ml-auto aspect-[4/5] w-[88%] overflow-hidden rounded-[2.5rem] border-[10px] border-blue-500 shadow-lift sm:w-[82%]">
            <img
              src="/slideshow6.jpg"
              alt="Profesor și elev lucrând împreună la un exercițiu de limba engleză în Centrul Progres"
              className="h-full w-full object-cover"
              loading="eager"
            />
          </div>
          <div className="absolute -left-1 bottom-12 max-w-[15rem] -rotate-3 rounded-[1.7rem] bg-paper p-5 text-ink shadow-lift sm:left-4">
            <Sparkles className="mb-3 h-7 w-7 text-coral" />
            <p className="font-display text-xl font-bold leading-tight">Explicații care fac lucrurile să se lege.</p>
          </div>
          <div className="absolute right-0 top-10 rotate-6 rounded-full bg-sun px-5 py-3 font-display text-sm font-bold text-ink shadow-float animate-drift">
            Română • Mate • Limbi străine
          </div>
        </div>
      </div>
    </section>

    <section aria-label="Cursuri disponibile" className="bg-ink text-paper">
      <div className="mx-auto grid max-w-7xl divide-y divide-paper/15 px-5 md:grid-cols-3 md:divide-x md:divide-y-0 lg:px-8">
        {([
          ['Limbi străine', 'Clasele I-XII', Languages],
          ['Limba română', 'Clasele V-XII', BookOpenCheck],
          ['Matematică', 'Clasele V-XII', Sigma]
        ] as const).map(([title, range, Icon]) => (
          <Link key={title} to="/packages" className="group flex items-center justify-between gap-5 px-2 py-7 md:px-7">
            <div className="flex items-center gap-4">
              <Icon className="h-7 w-7 text-sun" />
              <div>
                <h2 className="text-xl font-bold">{title}</h2>
                <p className="text-sm text-paper/60">{range}</p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        ))}
      </div>
    </section>

    <section className="bg-paper py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-[0.92fr_1.08fr] lg:px-8">
        <div className="relative">
          <img
            src="/slideshow2.jpg"
            alt="Lecție de limba spaniolă la tablă în Centrul Progres"
            loading="lazy"
            className="aspect-[4/5] w-full rounded-[2.25rem] object-cover shadow-lift"
          />
          <div className="absolute -bottom-7 right-5 max-w-xs rounded-[1.5rem] bg-coral p-6 text-white shadow-lift sm:right-[-2rem]">
            <p className="font-display text-2xl font-bold">Înțelegere înainte de memorare.</p>
          </div>
        </div>

        <div className="lg:pl-12">
          <p className="eyebrow">Cum lucrăm</p>
          <h2 className="mt-5 max-w-2xl font-display text-[clamp(2.8rem,5vw,5rem)] font-bold text-ink">
            Un loc în care întrebările sunt binevenite.
          </h2>
          <p className="mt-6 max-w-xl text-lg text-gray-600">
            Fiecare elev are alt ritm. De aceea, profesorii noștri explică, verifică și adaptează lecția până când informația devine clară și poate fi folosită cu încredere.
          </p>

          <div className="mt-10 space-y-7">
            {[
              ['Pornim de la nivelul real', 'Identificăm ce este deja clar și unde apar blocajele.'],
              ['Exersăm activ', 'Lecțiile combină explicația cu aplicarea imediată.'],
              ['Urmărim progresul', 'Ajustăm ritmul și dificultatea pe parcurs.']
            ].map(([title, text], index) => (
              <div key={title} className="grid grid-cols-[2.8rem_1fr] gap-4 border-b border-blue-100 pb-7 last:border-0">
                <span className="font-display text-2xl font-bold text-blue-600">0{index + 1}</span>
                <div>
                  <h3 className="text-xl font-bold text-ink">{title}</h3>
                  <p className="mt-1 text-gray-600">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="bg-blue-50 py-24 sm:py-32" id="cursuri">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="eyebrow">Cursuri pentru fiecare etapă</p>
            <h2 className="mt-5 max-w-3xl font-display text-[clamp(2.8rem,5vw,5rem)] font-bold text-ink">
              Materia potrivită. Formatul potrivit. Ritmul potrivit.
            </h2>
          </div>
          <Link to="/packages" className="inline-flex items-center gap-2 font-bold text-blue-700 hover:text-blue-900">
            Vezi toate prețurile <ArrowRight className="h-5 w-5" />
          </Link>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <article className="relative min-h-[31rem] overflow-hidden rounded-[2.25rem] bg-blue-600 p-8 text-white shadow-lift sm:p-12">
            <Languages className="h-10 w-10 text-sun" />
            <h3 className="mt-8 max-w-lg text-5xl font-bold">Limbi străine</h3>
            <p className="mt-5 max-w-lg text-lg text-blue-50">Engleză, spaniolă și alte limbi la cerere, în grupe sau individual.</p>
            <p className="mt-8 font-display text-2xl font-bold text-sun">De la 110 lei / ședință</p>
            <Link to="/packages" className="absolute bottom-8 left-8 inline-flex items-center gap-2 rounded-full bg-paper px-6 py-3 font-bold text-blue-700 sm:bottom-12 sm:left-12">
              Vezi opțiunile <ArrowRight className="h-5 w-5" />
            </Link>
            <img src="/slideshow7.jpg" alt="Elevă lucrând la un exercițiu de limbă străină" loading="lazy" className="absolute -bottom-12 -right-8 h-72 w-56 rotate-6 rounded-[2rem] object-cover shadow-lift sm:h-80 sm:w-64" />
          </article>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
            <article className="rounded-[2.25rem] bg-sun p-8 text-ink sm:p-10">
              <BookOpenCheck className="h-9 w-9" />
              <h3 className="mt-6 text-3xl font-bold">Limba română</h3>
              <p className="mt-3 text-ink/70">Grupe pentru clasele V-XII, ședințe de 2 ore.</p>
              <p className="mt-5 font-display text-xl font-bold">De la 180 lei / ședință</p>
            </article>
            <article className="rounded-[2.25rem] bg-coral p-8 text-white sm:p-10">
              <Sigma className="h-9 w-9" />
              <h3 className="mt-6 text-3xl font-bold">Matematică</h3>
              <p className="mt-3 text-white/80">Grupe pentru clasele V-XII, ședințe de 2 ore.</p>
              <p className="mt-5 font-display text-xl font-bold">De la 180 lei / ședință</p>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section className="bg-paper py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <p className="eyebrow">Simplu de început</p>
            <h2 className="mt-5 font-display text-[clamp(2.7rem,5vw,4.8rem)] font-bold">Trei pași până la prima lecție.</h2>
            <p className="mt-5 max-w-md text-gray-600">Fără formulare complicate. Ne spui ce cauți, discutăm nivelul și stabilim varianta potrivită.</p>
          </div>
          <ol className="divide-y divide-blue-100 border-y border-blue-100">
            {[
              ['Ne contactezi', 'Telefonic sau prin formular, cu materia și clasa elevului.'],
              ['Alegem formatul', 'Grupă sau individual, în funcție de disciplină și obiective.'],
              ['Începem cu un plan clar', 'Stabilim programul și obiectivele pentru următoarea perioadă.']
            ].map(([title, text], index) => (
              <li key={title} className="grid gap-3 py-7 sm:grid-cols-[4rem_1fr]">
                <span className="font-display text-3xl font-bold text-coral">{index + 1}.</span>
                <div><h3 className="text-2xl font-bold">{title}</h3><p className="mt-2 text-gray-600">{text}</p></div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>

    <section className="bg-ink py-24 text-paper sm:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <MessageCircleQuestion className="h-10 w-10 text-sun" />
            <h2 className="mt-7 font-display text-[clamp(2.8rem,5vw,5rem)] font-bold">Întrebări pe care le auzim des.</h2>
            <p className="mt-5 max-w-md text-paper/65">Nu ai găsit răspunsul? Suntem la un telefon distanță.</p>
          </div>
          <div className="divide-y divide-paper/15 border-y border-paper/15">
            {faqs.map((faq, index) => (
              <details key={faq.question} className="group py-6">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-5 font-display text-xl font-bold">
                  <span><span className="mr-3 text-sun">0{index + 1}</span>{faq.question}</span>
                  <span className="text-sun transition-transform duration-300 group-open:rotate-45" aria-hidden="true">+</span>
                </summary>
                <p className="max-w-2xl pb-2 pl-10 pt-4 text-paper/70">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="bg-paper py-20 text-ink sm:py-24">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-blue-50 px-6 py-16 text-center shadow-float sm:px-12 sm:py-20">
          <div className="absolute -right-14 -top-20 h-56 w-56 rounded-full bg-sun/65" aria-hidden="true" />
          <div className="absolute -bottom-16 -left-10 h-44 w-44 rounded-full bg-blue-200/70" aria-hidden="true" />
          <div className="relative mx-auto flex max-w-4xl flex-col items-center">
            <Users2 className="h-11 w-11 text-blue-700" />
            <h2 className="mt-6 font-display text-[clamp(2.8rem,6vw,5.5rem)] font-bold">Facem următorul pas împreună?</h2>
            <p className="mt-5 max-w-2xl text-lg text-ink/70">Spune-ne ce materie și ce clasă te interesează. Îți explicăm rapid formatele, prețurile și locurile disponibile.</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-7 py-4 font-bold text-white shadow-float hover:bg-blue-700">
                Cere detalii <ArrowRight className="h-5 w-5" />
              </Link>
              <a href="tel:0726423784" className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-4 font-bold text-ink shadow-float">
                <Clock3 className="h-5 w-5 text-blue-600" /> 0726 423 784
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default Home;
